import React from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import AppButton from '../../styles/common/AppButton';
import ColorSF2 from '../../styles/common/ColorSF2';
import {
  ReportInfoCard,
  SectionTitle,
  HeaderContainer,
  PageInfo,
  Section,
} from './styles/ReportInfoStyles';

/**
 * 報告書の各項目を表示するためのサブコンポーネント
 *
 * @param {string} titleId - メタ情報のタイトル(例: 作成者)
 * @param {(string|number)} content - 表示する内容(例: オプティム太郎)
 * @param {string} reportUrl - 報告書のURL
 *
 * @returns {ReactElement}
 */
const InfoItem = ({ titleId, content }) => {
  const isViews = titleId === 'report.detail.info.views';
  return (
    <div style={{ padding: '2px 0' }}>
      <span
        style={{
          display: 'block',
          fontWeight: 600,
          fontSize: '14px',
          marginBottom: '5px',
        }}
      >
        <FormattedMessage id={titleId} />
      </span>
      <span style={{ fontWeight: 400, fontSize: '14px', marginLeft: '15px' }}>
        {isViews ? (
          <>
            {content} <FormattedMessage id="report.detail.info.unit" />
          </>
        ) : (
          content
        )}
      </span>
    </div>
  );
};

InfoItem.propTypes = {
  titleId: PropTypes.string.isRequired,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

/**
 * 報告書の詳細情報を表示するカードコンポーネント
 *
 * - 報告書のメタ情報の表示
 * - 現在のページURLのコピー機能
 * - 閲覧しているpdfのページ番号の表示
 *
 * @param {Object} props コンポーネントのプロパティ
 * @param {number} props.totalPages - 報告書の総ページ数
 * @param {number} props.currentPage - 現在表示中のページ番号
 * @param {Object} props.report - 報告書の詳細情報
 * @param {string} props.report.number - 報告書No
 * @param {string} props.report.subject - 報告書の件名
 * @param {string} props.report.status - 報告書のステータス
 * @param {string} props.report.author - 報告書の作成者
 * @param {string} props.report.creationDate - 報告書の作成日時
 * @param {string} props.report.lastUpdatedBy - 報告書の最終更新者
 * @param {string} props.report.lastUpdatedDate - 最終更新日
 * @param {string} props.reportUrl - 報告書の署名付きURL
 *
 * @returns {ReactElement} 報告書の詳細情報を表示するカード。
 */
const ReportInfo = ({ totalPages, currentPage, report, reportUrl }) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const handleDownloadClick = () => {
    fetch(reportUrl)
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        // 現在の日付を取得
        const date = new Date();
        const year = date.getFullYear();
        const month = `0${date.getMonth() + 1}`.slice(-2);
        const day = `0${date.getDate()}`.slice(-2);

        // ファイル名を設定
        const subject = report?.subject || '報告書';
        const filename = `${subject}_${year}${month}${day}.pdf`;

        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {
        dispatch({
          type: 'OPEN_USER_PROMPT',
          promptMessage: [
            formatMessage({ id: 'userPrompt.message.reportDownload.error' }),
          ],
          buttonArray: [
            { name: formatMessage({ id: 'userPrompt.button.close' }) },
          ],
        });
      });
  };

  return (
    <ReportInfoCard>
      <Section>
        <HeaderContainer>
          <SectionTitle>
            <FormattedMessage id="report.detail.section.title" />
          </SectionTitle>
          {totalPages === null ? undefined : (
            <PageInfo>
              {formatMessage(
                { id: 'report.detail.pdf.page' },
                { currentPage, totalPages },
              )}
            </PageInfo>
          )}
        </HeaderContainer>
        <div style={{ margin: '15px 5px 5px ' }}>
          <AppButton
            bgColor={ColorSF2.white}
            color="black"
            borderColor={ColorSF2.mediumGray}
            type="button"
            bold
            onClick={handleDownloadClick}
          >
            <FormattedMessage id="report.detail.download" />
          </AppButton>
        </div>
      </Section>
      <Section>
        {Object.entries(report).map(([key, value]) => (
          <InfoItem
            key={key}
            titleId={`report.detail.info.${key}`}
            content={value}
          />
        ))}
      </Section>
    </ReportInfoCard>
  );
};
ReportInfo.propTypes = {
  totalPages: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  currentPage: PropTypes.number.isRequired,
  report: PropTypes.shape({
    number: PropTypes.string.isRequired,
    subject: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    author: PropTypes.string.isRequired,
    creationDate: PropTypes.string.isRequired,
    lastUpdatedBy: PropTypes.string.isRequired,
    lastUpdatedDate: PropTypes.string.isRequired,
  }).isRequired,
  reportUrl: PropTypes.string.isRequired,
};

ReportInfo.defaultProps = {
  totalPages: null,
};

export default ReportInfo;
